import { useStore } from '@store/appStore';

// `token` is only set once at login, but Graph tokens expire after ~1h - a long-open tab (e.g. the
// profile page's CheckForStudentUpdate polling) or one woken up after a night's sleep then kept
// sending an expired one: Graph answers 401 and the webservice can't verify it ("Unable to verify
// caller identity"). The auth provider registers how to silently get a new one, and it's swapped in
// shortly before expiry - by the webservice request interceptor (db/index.ts), the Graph helpers
// (graph.ts), and when a hidden tab becomes visible again (useAuthBase.ts).
let tokenRefresher: (() => Promise<string | null>) | null = null;
let refreshInFlight: Promise<string | null> | null = null;
let lastFailedRefreshAt = 0;
const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

// A renewal started just before a laptop/phone goes to sleep can hang for half an hour (seen in
// Bugsnag: the token request "in flight" for 16-31 min), holding back every request waiting on it.
// Give up after this, and don't start another attempt for a short while after a failed one - every
// request queued behind it would otherwise start its own and wait out its own timeout.
export const TOKEN_REFRESH_TIMEOUT_MS = 15 * 1000;
export const TOKEN_REFRESH_RETRY_AFTER_MS = 30 * 1000;

// Once Microsoft has answered "sign in again" (refresh token revoked/expired) - unlike a network
// error, nothing silent will fix that until the user signs in. Every request used to retry anyway:
// a 400 from the token endpoint plus a hidden Microsoft sign-in frame, every 30s while the "sign in
// again" prompt was already showing. Now only every few minutes (in case they signed in again in
// another tab, which makes the silent route work again).
export const LOGIN_REQUIRED_RETRY_AFTER_MS = 5 * 60 * 1000;
let renewalsPausedUntil = 0;

export function pauseRenewalsUntilSignIn() {
  renewalsPausedUntil = Date.now() + LOGIN_REQUIRED_RETRY_AFTER_MS;
}

export function resumeRenewals() {
  renewalsPausedUntil = 0;
  lastFailedRefreshAt = 0;
}

export function setTokenRefresher(refresher: (() => Promise<string | null>) | null) {
  tokenRefresher = refresher;
  lastFailedRefreshAt = 0;
  renewalsPausedUntil = 0;
}

function expiresWithin(jwt: string, marginMs: number): boolean {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp * 1000 - Date.now() < marginMs;
  } catch {
    return false;
  }
}

// True if the JWT's `exp` is past or within the refresh margin. Unreadable tokens count as fine.
export function tokenExpiresSoon(jwt: string): boolean {
  return expiresWithin(jwt, TOKEN_REFRESH_MARGIN_MS);
}

// True if the JWT has actually expired - no server will accept it any more. Unreadable tokens count
// as fine (not ours to judge).
export function tokenIsExpired(jwt: string): boolean {
  return expiresWithin(jwt, 0);
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
    );
  });
}

// The store's token, renewed first if it's expired or about to be. Falls back to the current one
// if it can't be renewed.
export async function getFreshToken(): Promise<string | null> {
  const { token, settoken } = useStore.getState();
  if (!token || !tokenRefresher || !tokenExpiresSoon(token)) return token;
  if (Date.now() < renewalsPausedUntil) return token;
  if (Date.now() - lastFailedRefreshAt < TOKEN_REFRESH_RETRY_AFTER_MS) return token;

  // Concurrent requests share one refresh.
  if (!refreshInFlight) {
    refreshInFlight = withTimeout(tokenRefresher(), TOKEN_REFRESH_TIMEOUT_MS)
      .then((result) => {
        if (!result) lastFailedRefreshAt = Date.now();
        return result;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  const refreshed = await refreshInFlight;
  if (refreshed) {
    if (refreshed !== useStore.getState().token) settoken(refreshed);
    return refreshed;
  }
  return token;
}

// For background polling: false when the request could only go out with an expired token that just
// couldn't be renewed - the webservice is certain to answer 403. Seen in App Insights: a teacher's PC
// left on the registration page in Teams sent ~1,900 of those Regi_CheckForChanges in a day. Skip the
// poll and try again next time; renewal is retried then too. No store token (mobile) - send as before.
export async function canSendWithToken(): Promise<boolean> {
  const token = await getFreshToken();
  return !token || !tokenIsExpired(token);
}

// When a token can't be renewed silently any more (the refresh token itself has expired), the only
// fix is signing in again - a reload does that: MSAL still knows the account from sessionStorage,
// so startup's getAccessToken() falls back to a redirect that usually bounces straight back via the
// user's Microsoft session. Guarded so a sign-in that keeps failing can't turn into a reload loop.
const AUTO_RELOAD_KEY = 'tokenRefresh.lastAutoReload';
const AUTO_RELOAD_MIN_INTERVAL_MS = 5 * 60 * 1000;

export function reloadToSignInAgain(reload: () => void = () => window.location.reload()): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(AUTO_RELOAD_KEY)) || 0;
  } catch {}
  if (Date.now() - last < AUTO_RELOAD_MIN_INTERVAL_MS) return false;

  try {
    sessionStorage.setItem(AUTO_RELOAD_KEY, String(Date.now()));
  } catch {}
  reload();
  return true;
}

// Inside an iframe (Attender embedded in a school portal, or a Teams website tab pointing at a web
// domain), Microsoft's login page can't be shown: MSAL refuses redirects there ("redirect_in_iframe").
// Signing in again has to go through a popup instead.
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin parent: accessing window.top throws - that's an iframe too.
    return true;
  }
}

// For callers handed a token explicitly (the Graph helpers): if it's the store's login token,
// swap in a fresh one. Anything else - e.g. mobile's user.access_token - is passed through as is.
export async function freshen(token: string | null | undefined): Promise<string | null | undefined> {
  if (token && token === useStore.getState().token) return getFreshToken();
  return token;
}
